import React, { useState, useEffect } from "react";
import { Button, TextField, Box, Paper, AccordionSummary } from "@mui/material";
import MDBox from "components/MDBox";
import ExportService from "./ExportService";
import MDTypography from "components/MDTypography";
import TennantService from 'services/TennantService';
import SupplierService from 'services/SupplierService';
import SearchAutocomplete from 'components/SearchAutocomplete';
import ThinkingSpinner from "components/ThinkingSpinner";

function FiltroExportacao({ onAlert, token }) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [type, setType] = useState("pedido");
  const [tennants, setTennants] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchTennantLoading, setSearchTennantLoading] = useState(false);
  const [searchSupplierLoading, setSearchSupplierLoading] = useState(false);
  const [selectedTennant, setSelectedTennant] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTennants = async () => {
      try {
        const [tennantResponse, supplierResponse] = await Promise.all([
          TennantService.GetTennants(token, null, { page: 1, pageSize: 150 }),
          SupplierService.GetSuppliers(token, { page: 1, pageSize: 150 })
        ]);
        setTennants(tennantResponse.data || []);
        setSuppliers(supplierResponse.data.data || []);
        setSelectedTennant(null);
        setSelectedSupplier(null);
      } catch (error) {
        console.error("Erro ao buscar tennants:", error);
        console.log('Status:', error.response?.status);
        console.log('Resposta da API:', error.response?.data);
        console.log('Request:', error.config?.data);
      } finally {
        setSearchTennantLoading(false);
      }
    };
    fetchTennants();
  }, []);

  const onTennantSearch = async (value) => {
    try {
      setSearchTennantLoading(true);
      const response = await TennantService.GetTennants(token, null, { page: 1, pageSize: 20, find: value === '' ? null : value });
      setTennants(response.data || []);
    } catch (error) {
      console.log('Status:', error.response?.status);
      console.log('Resposta da API:', error.response?.data);
      console.log('Request:', error.config?.data);
      setTennants([]);
    } finally {
      setSearchTennantLoading(false);
    }
  };

  const onSupplierSearch = async (value) => {
    try {
      setSearchSupplierLoading(true);
      const response = await SupplierService.GetSuppliers(token, { page: 1, pageSize: 20, find: value === '' ? null : value });
      setSuppliers(response.data.data);
    } catch (error) {
      console.log('Status:', error.response?.status);
      console.log('Resposta da API:', error.response?.data);
      console.log('Request:', error.config?.data);
      setSuppliers([]);
    } finally {
      setSearchSupplierLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';

    const [year, month, day] = date.split('-');

    return `${day}/${month}/${year}`;
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      onAlert("error", "Informe o intervalo de datas.");
      return;
    }
    setLoading(true);
    try {
      await ExportService.Export(token, { startDate, endDate, type, tennant: selectedTennant, supplier: selectedSupplier });
      onAlert("success", `${type}s exportad${type?.toLocaleLowerCase() === 'pedidos' ? 'os' : 'as'} com sucesso — período de ${formatDate(startDate)} a ${formatDate(endDate)}.`);
    } catch (error) {
      onAlert("error", error?.error[0] || "Erro ao exportar dados.");
      console.log('Erro ao exportar dados: ', error);
    }
    finally {
      setLoading(false);
    }
  };

  const LoadingOverlay = () => (<ThinkingSpinner />);

  return (
    <Paper sx={{
      top: 50,
      borderRadius: "12px",
      padding: "60px 10px 12px 10px",
      position: "relative",
      marginBottom: 10,
    }}>
      <AccordionSummary
        aria-controls="opcoes-content"
        id="opcoes-header"
        sx={{
          position: "absolute",
          width: "98%",
          top: -25,
          left: 17,
          background: "linear-gradient(195deg, #49a3f1, #1A73E8)",
          minHeight: "72px",
          borderRadius: "6px",
          marginBottom: 2,
          px: 2,

          "& .MuiAccordionSummary-content": {
            alignItems: "center",
          },

          "&.Mui-expanded": {
            minHeight: "72px",
          },

          "& .MuiAccordionSummary-content.Mui-expanded": {
            margin: "12px 0",
          },
        }}
      >
        <MDTypography variant="h6" color="white" fontWeight="medium">
          Exportar
        </MDTypography>
      </AccordionSummary>
      <Box
        mt={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          width: "100%",
          p: 3,
          top: 50,
        }}
      >
        <TextField
          label="Data Inicial"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: 150,
          }}
        />
        <TextField
          label="Data Final"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: 150,
          }}
        />
        <SearchAutocomplete
          label="Empresa"
          options={tennants}
          loading={searchTennantLoading}
          value={selectedTennant}
          getOptionLabel={(tennant) =>
            tennant
              ? `${tennant.Id} - ${tennant.CompanyName}`
              : ''
          }
          onSearch={onTennantSearch}
          onChange={(tennant) => {
            setSelectedTennant(tennant)
          }}
        />
        <SearchAutocomplete
          label="Fornecedor"
          options={suppliers}
          loading={searchSupplierLoading}
          value={selectedSupplier}
          getOptionLabel={(supplier) =>
            supplier
              ? `${supplier.Id} - ${supplier.CompanyName}`
              : ''
          }
          onSearch={onSupplierSearch}
          onChange={(supplier) => {
            setSelectedSupplier(supplier)
          }}
        />
        <TextField
          label="Tipo"
          select
          SelectProps={{ native: true }}
          value={type}
          onChange={(e) => setType(e.target.value)}
          sx={{ minWidth: 100 }}
        >
          <option value="pedido">Pedido</option>
          <option value="oferta">Oferta</option>
        </TextField>
        <Button
          variant="contained"
          disabled={loading}
          sx={{
            minWidth: "100px",
            background: "linear-gradient(90deg, #1976D2 0%, #42A5F5 100%)",
            color: "#fff",
            fontWeight: "bold",
            textTransform: "uppercase",
            borderRadius: "12px",
            padding: "8px 20px",
            boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.16)",
            "&:hover": {
              background: "linear-gradient(90deg, #1565c0 0%, #1e88e5 100%)",
            },
          }}
          onClick={handleExport}
        >
          {(loading && <LoadingOverlay />) || "Exportar"}
        </Button>
      </Box>
    </Paper>
  );
}

export default FiltroExportacao;
